'use client'

import React from 'react'
import classNames from 'classnames'
import RichText from '@/ui/components/RichText'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { Label } from '@/ui/atoms/Label'
import { Icon } from '@/ui/atoms/Icon'
import { useTranslations } from 'next-intl'

type TitleTextProps = {
  title: string | null | undefined
  text: SerializedEditorState | null | undefined
  className?: string
  facts?: {
    inputs?: { value: string; isOptional?: boolean | null; id?: string | null }[] | null
    outputs?: { value: string; isOptional?: boolean | null; id?: string | null }[] | null
  }
  materialStreams?: { value: string; isOptional?: boolean | null; id?: string | null }[] | null
  icon?: string | null
  isSolutionHero?: boolean
  afterDescription?: React.ReactNode
  /** pre-rendered body (used by the static P2 pages whose prose is audited HTML, not Lexical) */
  children?: React.ReactNode
}

export const TitleText: React.FC<TitleTextProps> = ({
  title,
  text,
  className,
  facts,
  materialStreams,
  icon,
  isSolutionHero = false,
  afterDescription,
  children,
}) => {
  const t = useTranslations('solutions')
  const hasFactsOrStreams =
    (facts?.inputs && facts.inputs.length > 0) ||
    (facts?.outputs && facts.outputs.length > 0) ||
    (materialStreams && materialStreams.length > 0)

  return (
    <div
      className={classNames(
        'lg:p-card-s p-mobile-card-s border border-border-subtle rounded-md',
        className,
      )}
    >
      <p className="text-body-XS-regular font-semibold text-text-faded mb-2">{title}</p>
      {text && (
        <RichText
          className="!text-body-M-regular text-text-base"
          data={text}
          enableGutter={false}
          enableProse={true}
        />
      )}
      {children}
      {afterDescription && (
        <div className="mt-6 pt-6 border-t border-border-subtle">
          {afterDescription}
        </div>
      )}
      {hasFactsOrStreams && (
        <div className="mt-6 pt-6 border-t border-border-subtle">
          <div className="flex flex-col gap-6">
            {materialStreams && materialStreams.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Icon name={icon} className="text-text-faded" />
                  <p className="text-label-XS-semibold text-text-faded">{t('materialStreams')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {materialStreams.map((stream, i) => (
                    <Label
                      key={i}
                      title={stream.value}
                      variant="subtle-blue"
                      size="small"
                      className="text-label-S-regular"
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {facts?.inputs && facts.inputs.length > 0 && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <Icon name="inbox-archive-fill" className="text-text-faded" />
                    <p className="text-label-XS-semibold text-text-faded">Inputs</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap gap-2">
                      {facts.inputs
                        .filter((fact) => !fact.isOptional)
                        .map((fact, i) => (
                          <Label
                            key={i}
                            title={fact.value}
                            variant="subtle-blue"
                            size="small"
                            className="text-label-S-regular"
                          />
                        ))}
                    </div>
                    {facts.inputs.some((fact) => fact.isOptional) && (
                      <>
                        <p className="text-label-XS-regular text-text-faded">Optional</p>
                        <div className="flex flex-wrap gap-2">
                          {facts.inputs
                            .filter((fact) => fact.isOptional)
                            .map((fact, i) => (
                              <Label
                                key={i}
                                title={fact.value}
                                variant="subtle-blue"
                                size="small"
                                className="text-label-S-regular"
                              />
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {facts?.outputs && facts.outputs.length > 0 && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <Icon name="inbox-unarchive-fill" className="text-text-faded" />
                    <p className="text-label-XS-semibold text-text-faded">Outputs</p>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                      {facts.outputs
                        .filter((fact) => !fact.isOptional)
                        .map((fact, i) => (
                          <Label
                            key={i}
                            title={fact.value}
                            variant="light-blue"
                            size="small"
                            className="text-label-S-regular"
                          />
                        ))}
                    </div>
                    {facts.outputs.some((fact) => fact.isOptional) && (
                      <React.Fragment>
                        <p className="text-label-XS-regular text-text-faded">Optional</p>
                        <div className="flex flex-wrap gap-2">
                          {facts.outputs
                            .filter((fact) => fact.isOptional)
                            .map((fact, i) => (
                              <Label
                                key={i}
                                title={fact.value}
                                variant="light-blue"
                                size="small"
                                className="text-label-S-regular"
                              />
                            ))}
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
